/** @format */

import { HomeHero } from '@/components/Hero';
import { PageWrapper } from '@/components/PageWrapper';
import { Loader } from '@/components/ui/Loader';
import { useLanguageSwitcherContext } from '@/context/LanguageSwitcherContext';
import { usePageContent } from '@/hooks/usePageContent';
import {
  getAllBlogPosts,
  getAllNews,
  getCompany,
  getFooter,
  getNavigation,
  getPage,
  getTranslations,
} from '@/lib/contenful.api';
import { getAllNewsSlugs } from '@/lib/page-generation';
import { BlogPostCard, NewsCard } from '@/types/news';
import { Company, HomePageContent, Menu, Translations } from '@/types/page';
import { Entry } from 'contentful';
import { GetStaticProps, NextPage } from 'next';
import dynamic from 'next/dynamic';
import { useEffect } from 'react';

const PageContent = dynamic(() => import('@/components/PageContent').then(mod => mod.PageContent), {
  loading: () => <Loader />,
});

const Index: NextPage<Props> = ({
  content,
  navigation,
  footer,
  company,
  translations,
  allNews,
  allBlogPosts,
  newsSlugs,
}) => {
  const { setSlugs } = useLanguageSwitcherContext();
  const pageContent = usePageContent(content);

  useEffect(() => {
    const HomeSlugs = {
      en: '',
      it: '',
      es: '',
      fr: '',
      pt: '',
    };
    setSlugs(HomeSlugs);
  }, []);

  return (
    <PageWrapper
      meta={content.seo.fields}
      pageType="business"
      navigation={navigation}
      footer={footer}
      company={company}
      translations={translations}
      slug={content.slug.fields.slug}
    >
      <HomeHero title={content.body} image={content.image} />
      <PageContent
        content={pageContent}
        allBlogPosts={allBlogPosts}
        allNews={allNews}
        newsSlugs={newsSlugs!}
        title="Joivy Business"
      />
    </PageWrapper>
  );
};

export default Index;

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const localeToFetch = () => {
    if (!locale) return 'en';
    if (locale === 'default') return 'en';
    return locale;
  };
  const content = await getPage('homePage', localeToFetch(), '/');
  const navigation = await getNavigation(localeToFetch());
  const footer = await getFooter(localeToFetch());
  const company = await getCompany(localeToFetch());
  const translations = await getTranslations(localeToFetch());
  const allNews = await getAllNews(localeToFetch());
  const allBlogPosts = await getAllBlogPosts(localeToFetch(), 6);
  const newsSlugs = await getAllNewsSlugs();

  return {
    notFound: locale === 'default',
    props: {
      key: `${locale}/business`,
      content,
      navigation,
      footer,
      company,
      translations,
      allNews,
      allBlogPosts,
      newsSlugs: newsSlugs[0].fields.slug,
    },
  };
};

type Props = {
  content: HomePageContent;
  navigation: Entry<Menu>;
  footer: Entry<Menu>;
  company: Entry<Company>;
  translations: Entry<Translations>;
  allNews: NewsCard[];
  allBlogPosts: BlogPostCard[];
  newsSlugs: {
    [key: string]: string;
  };
};
